import { Alert, Button } from '@mui/material'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Activity, Contact, History, KeyRound, ScrollText, Users } from 'lucide-react'
import type { Can, Edition, NavItem } from '@agribot/polygon-checks-ui'
import { NavigationGuardProvider } from '@agribot/polygon-checks-ui'
import type { AuthProviders } from '@agribot/polygon-checks-ui'
import { apiUrl } from '@agribot/polygon-checks-ui'
import { Usage } from './Usage'
import { People } from './People'
import { Keys } from './Keys'
import { KeyUsers } from './KeyUsers'
import { PartnerUsers } from './PartnerUsers'
import { Logs } from './Logs'
import { Activity as ActivityLog } from './Activity'

/**
 * What our own edition has and a partner's does not: the admin section (a
 * partner root key's users, the organisation's people, usage, the
 * platform's keys and request logs) and signing in through Auth0 or with a
 * key. Handed to `startPolygonChecks` as `fullEdition`; an application
 * without this package has none of it.
 */

/** The rail's Admin group. A group with nothing visible in it disappears. */
const adminNav: NavItem[] = [
  // A root key manages this organisation's users; a person never sees
  // this screen, because people are not provisioned that way.
  { to: '/admin/users', label: 'Users', Icon: Users, show: (c) => c.key },
  { to: '/admin/people', label: 'People', Icon: Contact, show: (c) => c.org },
  { to: '/admin/activity', label: 'Activity', Icon: History, show: (c) => c.org && !c.anyKey },
  { to: '/admin/usage', label: 'Usage', Icon: Activity, show: (c) => c.org || c.anyKey },
  { to: '/admin/keys', label: 'Keys', Icon: KeyRound, show: (c) => c.platform },
  { to: '/admin/logs', label: 'Logs', Icon: ScrollText, show: (c) => c.platform },
]

/** A platform key reaches the admin routes and nothing else — no regions,
 *  no plots, because it belongs to no organisation to hold any. */
function PlatformKeyApp() {
  return (
    <NavigationGuardProvider>
      <Routes>
        <Route path="/admin/keys" element={<Keys />} />
        <Route path="/admin/keys/:keyId" element={<KeyUsers />} />
        <Route path="/admin/usage" element={<Usage />} />
        <Route path="/admin/logs" element={<Logs />} />
        <Route path="*" element={<Navigate to="/admin/keys" replace />} />
      </Routes>
    </NavigationGuardProvider>
  )
}

/** A partner's root key reaches two screens. Rendering the ordinary shell
 *  for it would be a rail of links that all answer 403, so it gets its own. */
function RootKeyApp() {
  return (
    <NavigationGuardProvider>
      <Routes>
        <Route path="/admin/users" element={<PartnerUsers />} />
        <Route path="/admin/usage" element={<Usage />} />
        <Route path="*" element={<Navigate to="/admin/users" replace />} />
      </Routes>
    </NavigationGuardProvider>
  )
}

/**
 * The admin routes inside a person's shell. The API refuses anyone else on
 * them, so a route reached by hand is a dead end rather than a leak; leaving
 * them out of the router as well means it reads as "no such page" instead
 * of an error.
 */
function adminRoutes(can: Can) {
  return [
    can.org && <Route key="people" path="/admin/people" element={<People />} />,
    can.org && <Route key="activity" path="/admin/activity" element={<ActivityLog />} />,
    can.org && <Route key="usage" path="/admin/usage" element={<Usage />} />,
    can.platform && <Route key="keys" path="/admin/keys" element={<Keys />} />,
    can.platform && <Route key="key-users" path="/admin/keys/:keyId" element={<KeyUsers />} />,
    can.platform && <Route key="logs" path="/admin/logs" element={<Logs />} />,
  ]
}

/**
 * Signing in as a person. A full page navigation, not fetch: the browser has
 * to follow the redirect to the identity provider and back.
 */
export function PersonSignIn({ providers }: { providers: AuthProviders | undefined }) {
  if (!providers) return null
  if (providers.auth0) {
    return (
      <Button fullWidth onClick={() => { window.location.href = apiUrl('/auth/auth0/start') }}>
        Continue with Auth0
      </Button>
    )
  }
  return (
    <Alert severity="info">
      No sign-in is configured on this deployment. Set <code>AUTH0_DOMAIN</code> on
      the API, or run it with <code>ALLOW_ANONYMOUS</code> for local development.
      An access token works either way.
    </Alert>
  )
}

/** Ours: key sign-in and the admin section. Signing in is the SignIn screen's. */
export const fullEdition: Edition = {
  adminNav,
  adminRoutes,
  RootKeyApp,
  PlatformKeyApp,
}
